// src/hooks/useCustomers.js
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

const useCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!user?.uid) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'customerProfiles'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            notes: data.notes || '',
            createdAt: data.createdAt?.toDate() || null,
            updatedAt: data.updatedAt?.toDate() || null,
            createdBy: data.createdBy || '',
          };
        });
        setCustomers(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading customers:', err);
        setError('Failed to load customers');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const addCustomer = useCallback(
    async (data) => {
      setError(null);
      try {
        const docRef = await addDoc(collection(db, 'customerProfiles'), {
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          email: data.email.trim(),
          phone: data.phone?.trim() || '',
          address: data.address?.trim() || '',
          notes: data.notes?.trim() || '',
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        return { success: true, id: docRef.id };
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [user]
  );

  const updateCustomer = useCallback(async (id, data) => {
    setError(null);
    try {
      await updateDoc(doc(db, 'customerProfiles', id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteCustomer = useCallback(async (id) => {
    setError(null);
    try {
      await deleteDoc(doc(db, 'customerProfiles', id));
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    customers,
    loading,
    error,
    clearError,
    addCustomer,
    updateCustomer,
    deleteCustomer,
  };
};

export default useCustomers;